import type { BrandKitBlockId } from './brandKitBlocks'
import type { Platform } from './creatorsApi'
// Brand link + PDF export — 발급·목록·회수·기록 클라 API. 서버 계약: routes/brandLinks.ts.
export interface BrandLinkItem {
  token: string
  creatorId: string
  creatorName: string
  brandName: string
  createdBy: string
  createdAt: string
  expiresAt: string
  revokedAt: string | null
  viewCount: number
  lastViewedAt: string | null
  url: string
  status: 'active' | 'expired' | 'revoked'
}
export interface IssuedBrandLink {
  token: string
  url: string
  expiresAt: string
}
// PDF export 기록 — 링크와 동형 (누가·언제·어느 브랜드용 + 재다운로드 URL)
export interface PdfExportItem {
  id: string
  creatorId: string
  creatorName: string
  brandName: string | null
  createdBy: string
  createdAt: string
  downloadCount: number
  lastDownloadedAt: string | null
  fileUrl: string
}
export interface CreatedPdfExport {
  id: string
  fileUrl: string
}

interface Envelope<T> {
  success: boolean
  data?: T
  errorMessage?: string
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { Accept: 'application/json', ...(init?.body ? { 'Content-Type': 'application/json' } : {}) },
    ...init,
  })
  let body: Envelope<T> | null = null
  try {
    body = (await res.json()) as Envelope<T>
  } catch {
    /* non-JSON */
  }
  if (!res.ok || !body?.success) throw new Error(body?.errorMessage ?? '요청에 실패했습니다.')
  return body.data as T
}

export function issueBrandLink(
  creatorId: string,
  brandName: string,
  blocks?: BrandKitBlockId[],
  platforms?: Platform[],
): Promise<IssuedBrandLink> {
  return call(`/api/creators/${encodeURIComponent(creatorId)}/brand-links`, {
    method: 'POST',
    body: JSON.stringify({ brandName, blocks, platforms }),
  })
}
export function listBrandLinks(params: {
  status?: 'all' | 'active' | 'expired' | 'revoked'
  q?: string
  cursor?: string
}): Promise<{ items: BrandLinkItem[]; nextCursor: string | null }> {
  const qs = new URLSearchParams()
  if (params.status && params.status !== 'all') qs.set('status', params.status)
  if (params.q) qs.set('q', params.q)
  if (params.cursor) qs.set('cursor', params.cursor)
  return call(`/api/brand-links${qs.toString() ? `?${qs}` : ''}`)
}
export async function revokeBrandLink(token: string): Promise<void> {
  await call(`/api/brand-links/${encodeURIComponent(token)}/revoke`, { method: 'POST' })
}
/** 회수 취소 — 같은 URL이 다시 활성화된다 (만료일은 원래 값 유지). */
export async function restoreBrandLink(token: string): Promise<void> {
  await call(`/api/brand-links/${encodeURIComponent(token)}/restore`, { method: 'POST' })
}

/** Export PDF — 스냅샷 생성 + 기록. 반환된 fileUrl을 새 탭으로 열면 인쇄 다이얼로그가 뜬다. */
export function createPdfExport(
  creatorId: string,
  brandName?: string,
  blocks?: BrandKitBlockId[],
  platforms?: Platform[],
): Promise<CreatedPdfExport> {
  return call(`/api/creators/${encodeURIComponent(creatorId)}/pdf-exports`, {
    method: 'POST',
    body: JSON.stringify({ brandName: brandName ?? '', blocks, platforms }),
  })
}
export function listPdfExports(params: {
  q?: string
  cursor?: string
}): Promise<{ items: PdfExportItem[]; nextCursor: string | null }> {
  const qs = new URLSearchParams()
  if (params.q) qs.set('q', params.q)
  if (params.cursor) qs.set('cursor', params.cursor)
  return call(`/api/pdf-exports${qs.toString() ? `?${qs}` : ''}`)
}

/** yyyy-MM-dd HH:mm (사용자 확정 포맷) */
export function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}
